import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { s } from '../mdm/_mdmKit.js';
import {
  listReleasedOrders,
  listMyReleasedOrders,
  type ReleasedOrder,
} from '../../api/vendorProfileApi.js';
import { acceptOrderLine, startOrderCompletion } from '../../api/orderWorkApi.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface ReleasedOrdersTableProps {
  /**
   * The vendor whose released orders are shown, as opened from his profile.
   * Omitted on the vendor's own portal, where the backend reads the vendor
   * from the login.
   */
  readonly vendorId?: string;
  readonly titleKey: string;
  readonly emptyKey: string;
  /** When given, the host screen shows the failure; otherwise it shows here. */
  readonly onError?: (message: string) => void;
}

/**
 * The branding orders released to a vendor.
 *
 * The data is exactly what the release to the vendor already wrote on each
 * order line. The vendor profile and the vendor's own dashboard both render
 * this one table over the one read, so the two can never disagree. On his own
 * dashboard the vendor also works an order that needs no dealer from here:
 * accept it, then report it completed with proofs.
 */
export function ReleasedOrdersTable({
  vendorId,
  titleKey,
  emptyKey,
  onError,
}: ReleasedOrdersTableProps): ReactNode {
  const { decimal } = useBusinessNumber();
  const { t: tr } = useTranslation();
  // forwardedAt is an instant, so it is read on the reader's clock and written
  // in the tenant's DISPLAY_DATE_FORMAT rather than sliced off an ISO string.
  const { atDay } = useBusinessDay();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<readonly ReleasedOrder[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyLine, setBusyLine] = useState<string | null>(null);
  // Only the vendor himself acts on his orders; his profile, opened by the
  // branding team, stays a read.
  const ownPortal = !vendorId;

  const load = useCallback(() => {
    setError(null);
    return (vendorId ? listReleasedOrders(vendorId, token) : listMyReleasedOrders(token))
      .then((r) => setRows(r))
      .catch((e) => {
        const message = (e as Error).message;
        setError(message);
        onError?.(message);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId, token]);

  useEffect(() => {
    setRows(null);
    void load();
  }, [load]);

  async function accept(r: ReleasedOrder): Promise<void> {
    setActionError(null);
    setBusyLine(r.requestLineId);
    try {
      await acceptOrderLine(r.requestLineId, token);
      await load();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusyLine(null);
    }
  }

  async function completed(r: ReleasedOrder): Promise<void> {
    setActionError(null);
    setBusyLine(r.requestLineId);
    try {
      const c = await startOrderCompletion(r.requestLineId, token);
      navigate(`/vendor-ops/order-completions/${c.completionId}`);
    } catch (e) {
      setActionError((e as Error).message);
      setBusyLine(null);
    }
  }

  function action(r: ReleasedOrder): ReactNode {
    if (!r.completedByVendor) return '\u2014';
    const busy = busyLine === r.requestLineId;
    if (r.status === 'FORWARDED_TO_VENDOR') {
      return (
        <Button type="button" variant="primary" disabled={busy}
                data-testid={`accept-${r.requestLineId}`} onClick={() => void accept(r)}>
          {tr('vops.orderWork.accept')}
        </Button>
      );
    }
    // An open completion is opened; none (or a rejected one) starts afresh.
    if (r.completionId && r.completionStatus !== 'REJECTED') {
      return (
        <Button type="button" variant="secondary"
                data-testid={`open-completion-${r.requestLineId}`}
                onClick={() => navigate(`/vendor-ops/order-completions/${r.completionId}`)}>
          {tr('vops.orderWork.open')}
        </Button>
      );
    }
    if (r.status === 'IN_PROGRESS') {
      return (
        <Button type="button" variant="primary" disabled={busy}
                data-testid={`job-completed-${r.requestLineId}`} onClick={() => void completed(r)}>
          {tr('vops.orderWork.jobCompleted')}
        </Button>
      );
    }
    return '\u2014';
  }

  return (
    <section aria-label="released-orders" style={s.card}>
      <h3 style={{ fontSize: 14, marginTop: 0 }}>{tr(titleKey)}</h3>
      {actionError ? <p role="alert" data-testid="released-orders-action-error">{actionError}</p> : null}
      {error !== null ? (
        onError ? null : (
          <p role="alert" data-testid="released-orders-error">{error}</p>
        )
      ) : rows === null ? (
        <p data-testid="released-orders-loading">{tr('vendorProfile.loading')}</p>
      ) : rows.length === 0 ? (
        <p data-testid="released-orders-empty" style={{ color: s.muted }}>
          {tr(emptyKey)}
        </p>
      ) : (
        <DataTable
          rows={rows as ReleasedOrder[]}
          rowKey={(r) => r.requestLineId}
          rowTestId={(r) => `released-${r.requestLineId}`}
          ariaLabel="released-orders-table"
          columns={[
            { key: 'requestNo', header: tr('vendorProfile.roOrder'), render: (r) => `${r.requestNo ?? '\u2014'}${r.lineNumber != null ? ` / ${r.lineNumber}` : ''}` },
            { key: 'dealer', header: tr('vendorProfile.roDealer'), render: (r) => r.dealerName ? `${r.dealerName}${r.dealerCode ? ` (${r.dealerCode})` : ''}` : '\u2014' },
            { key: 'branding', header: tr('vendorProfile.roBranding'), render: (r) => `${r.typeName ?? '\u2014'}${r.itemName ? ` / ${r.itemName}` : ''}` },
            { key: 'qty', header: tr('vendorProfile.roQty'), render: (r) => `${decimal(r.quantity)}${r.uomCode ? ` ${r.uomCode}` : ''}` },
            { key: 'wo', header: tr('vendorProfile.roWo'), render: (r) => r.woNumber ?? '\u2014' },
            { key: 'forwardedAt', header: tr('vendorProfile.roReleasedOn'), render: (r) => atDay(r.forwardedAt) },
            { key: 'status', header: tr('vops.orderWork.colStatus'), render: (r) => r.completionStatus ? `${r.status ?? ''} · ${tr(`vops.orderWork.status.${r.completionStatus}`)}` : (r.status ?? '\u2014') },
            ...(ownPortal
              ? [{ key: 'action', header: tr('vops.orderWork.colAction'), render: (r: ReleasedOrder) => action(r) }]
              : []),
          ]}
        />
      )}
    </section>
  );
}
